/**
 * uploadMedia — send a file the person picked off their own device to
 * /api/media/upload. Anything over the single-shot limit is sliced so the
 * ingress does not reject the body; a 1GB video works the same as a 200KB logo.
 *
 * Returns the stored asset: { url, path, filename, size, kind, width, height }.
 */
import api, { getErrorMessage } from '@/lib/api';

const SINGLE_SHOT_LIMIT = 24 * 1024 * 1024;
const CHUNK_SIZE = 4 * 1024 * 1024;

export const PURPOSE_ACCEPT = {
  logo: 'image/*,.svg',
  favicon: 'image/*,.ico,.svg',
  hero: 'image/*,video/mp4,video/webm,video/quicktime',
  banner: 'image/*',
  cover: 'image/*',
  avatar: 'image/*',
  photo: 'image/*',
  product: 'image/*',
  gallery: 'image/*,video/mp4,video/webm,video/quicktime',
  video: 'video/mp4,video/webm,video/quicktime,.mkv,.avi,.m4v',
  audio: 'audio/*',
  document: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.rtf,.zip,image/*',
  general: 'image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip',
};

export const acceptFor = (purpose) => PURPOSE_ACCEPT[purpose] || PURPOSE_ACCEPT.general;

const single = async (file, purpose, onProgress) => {
  const form = new FormData();
  form.append('file', file);
  form.append('purpose', purpose);
  const { data } = await api.post('/media/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  return data;
};

const chunked = async (file, purpose, onProgress) => {
  const { data: session } = await api.post('/media/upload/init', {
    filename: file.name, total_size: file.size, purpose,
  });
  const size = session.chunk_size || CHUNK_SIZE;
  const total = Math.ceil(file.size / size);
  try {
    for (let i = 0; i < total; i += 1) {
      const form = new FormData();
      form.append('index', String(i));
      form.append('chunk', file.slice(i * size, Math.min((i + 1) * size, file.size)));
      // eslint-disable-next-line no-await-in-loop
      await api.post(`/media/upload/${session.upload_id}/chunk`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (onProgress) onProgress(Math.round(((i + 1) / total) * 96));
    }
    const { data } = await api.post(`/media/upload/${session.upload_id}/complete`);
    if (onProgress) onProgress(100);
    return data;
  } catch (err) {
    api.delete(`/media/upload/${session.upload_id}`).catch(() => {});
    throw err;
  }
};

export const uploadMedia = async (file, { purpose = 'general', onProgress } = {}) => {
  if (!file) throw new Error('Nothing to upload');
  const limit = SINGLE_SHOT_LIMIT;
  try {
    return file.size > limit
      ? await chunked(file, purpose, onProgress)
      : await single(file, purpose, onProgress);
  } catch (err) {
    throw new Error(getErrorMessage(err, 'That file could not be uploaded'));
  }
};

export default uploadMedia;
